import { Link } from 'react-router-dom';
import { Plus, Users, Music, Image, MoreVertical } from 'lucide-react';
import { usePersonas } from '../hooks/usePersona';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

export default function Personas() {
  const { personas, isLoading, error } = usePersonas();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-jelly-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Personas</h1>
          <p className="text-zinc-400 mt-1">Manage your artist personas</p>
        </div>
        <Link to="/admin/personas/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            New Persona
          </Button>
        </Link>
      </div>

      {/* Empty state */}
      {personas.length === 0 ? (
        <Card className="text-center py-12">
          <Users className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No personas yet</h3>
          <p className="text-zinc-400 mb-6 max-w-md mx-auto">
            Create your first artist persona to start generating AI-powered music videos that match your unique style.
          </p>
          <Link to="/admin/personas/new">
            <Button size="lg">
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Persona
            </Button>
          </Link>
        </Card>
      ) : (
        /* Personas grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {personas.map((persona) => (
            <Link key={persona.id} to={`/admin/personas/${persona.id}`}>
              <Card className="h-full hover:border-zinc-700 transition-colors cursor-pointer group">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-jelly-500 to-purple-600 flex items-center justify-center text-2xl font-bold">
                    {persona.artist_name[0]}
                  </div>
                  <button
                    className="p-1 rounded-lg hover:bg-zinc-800 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.preventDefault();
                      // Menu handler
                    }}
                  >
                    <MoreVertical className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>

                <h3 className="text-lg font-semibold mb-1">{persona.artist_name}</h3>
                {persona.stage_name && <p className="text-zinc-400 text-sm mb-3">{persona.stage_name}</p>}

                <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
                  <span className="flex items-center gap-1">
                    <Music className="w-4 h-4" />0 tracks
                  </span>
                  <span className="flex items-center gap-1">
                    <Image className="w-4 h-4" />0 images
                  </span>
                </div>

                {/* Confidence meter */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-400">Persona confidence</span>
                    <span className="text-zinc-300">
                      {Math.round((persona.current_memory?.overall_confidence || 0) * 100)}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-jelly-500 to-purple-500 transition-all"
                      style={{ width: `${(persona.current_memory?.overall_confidence || 0) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between text-xs text-zinc-500">
                  <span>Created {new Date(persona.created_at).toLocaleDateString()}</span>
                  <span className="capitalize">{persona.creation_method}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
